/**
 * WorkbenchToolbar — shared button primitives for the Workbench header action row.
 * Neutral secondary styling with one primary CTA; grouped actions; accessible focus rings.
 */
import React from 'react';
import { clsx } from 'clsx';

export const TOOLBAR_FOCUS =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

export type ToolbarVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'icon' | 'destructive';

interface ToolbarButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ToolbarVariant;
    icon?: React.ReactNode;
    /** Visible label; hidden below `labelFrom` breakpoint when set. */
    label?: string;
    labelFrom?: 'md' | 'lg' | 'xl';
    /** Pressed / active toggle state (e.g. filter open, demo guide). */
    active?: boolean;
    /** When true, omit outer padding for use inside ToolbarGroup. */
    grouped?: boolean;
}

const variantClasses: Record<ToolbarVariant, string> = {
    primary:
        'bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:hover:bg-primary',
    secondary:
        'bg-card text-foreground border border-border hover:bg-muted hover:text-accent-foreground disabled:opacity-50',
    outline:
        'bg-card text-foreground border border-border hover:bg-muted hover:text-accent-foreground disabled:opacity-50',
    ghost:
        'text-foreground/80 bg-canvas-surface/50 border border-transparent hover:text-foreground hover:bg-canvas-surface/80 hover:border-border/50 disabled:opacity-40',
    icon:
        'text-muted-foreground hover:text-foreground hover:bg-canvas-surface/80 disabled:opacity-30 p-1.5',
    destructive:
        'text-muted-foreground hover:text-destructive hover:bg-destructive/10 disabled:opacity-30 p-1.5',
};

const labelBreakpoint: Record<'md' | 'lg' | 'xl', string> = {
    md: 'inline',
    lg: 'hidden md:inline',
    xl: 'hidden lg:inline',
};

export const ToolbarButton = React.forwardRef<HTMLButtonElement, ToolbarButtonProps>(
    (
        {
            variant = 'secondary',
            icon,
            label,
            labelFrom = 'md',
            active = false,
            grouped = false,
            className,
            children,
            type = 'button',
            ...props
        },
        ref,
    ) => {
        const isIconOnly = variant === 'icon' || variant === 'destructive';
        const showLabel = label && !isIconOnly;

        return (
            <button
                ref={ref}
                type={type}
                className={clsx(
                    TOOLBAR_FOCUS,
                    'inline-flex items-center justify-center gap-1.5 rounded-md text-xs font-medium transition-colors disabled:cursor-not-allowed',
                    isIconOnly ? 'rounded-md' : grouped ? 'rounded-sm px-2.5 py-1.5' : 'px-2.5 py-1.5',
                    variantClasses[variant],
                    grouped && variant === 'outline' && 'border-transparent hover:border-transparent',
                    active && variant !== 'primary' && 'bg-accent text-accent-foreground',
                    className,
                )}
                {...props}
            >
                {icon && <span className="shrink-0 [&>svg]:w-4 [&>svg]:h-4">{icon}</span>}
                {showLabel && (
                    <span className={labelBreakpoint[labelFrom]}>{label}</span>
                )}
                {children}
            </button>
        );
    },
);
ToolbarButton.displayName = 'ToolbarButton';

interface ToolbarGroupProps {
    children: React.ReactNode;
    /** Accessible name for the group (e.g. "Graph tracing"). */
    label: string;
    className?: string;
}

/** Visually groups related toolbar actions with a shared neutral container. */
export const ToolbarGroup: React.FC<ToolbarGroupProps> = ({ children, label, className }) => (
    <div
        role="group"
        aria-label={label}
        className={clsx(
            'flex items-center gap-0.5 rounded-md border border-border bg-card p-0.5 shrink-0',
            className,
        )}
    >
        {children}
    </div>
);

/** Vertical separator between toolbar sections. */
export const ToolbarDivider: React.FC = () => (
    <div className="w-px h-5 bg-border/80 mx-0.5 shrink-0 self-center" aria-hidden />
);
